import { useEffect, useState } from 'react';

/**
 * Janela de atendimento da Meta: mensagem livre só vai até 24 horas depois da
 * última mensagem DO CLIENTE; depois disso, só template aprovado. A conta mora na
 * API (`JanelaAtendimento.cs`), que manda junto das mensagens se a regra vale para
 * a conexão (Evolution não tem janela) e quando ela fecha. A API também recusa o
 * envio fora da janela — aqui é só para o compositor travar antes da tentativa.
 *
 * `null` = ainda não se sabe (conversa carregando): não trava.
 */
export interface JanelaAtendimento {
  aplica: boolean;
  /** Epoch ms em que a janela fecha; `null` = o cliente nunca escreveu. */
  fechaEm: number | null;
}

export const DURACAO_JANELA_MS = 24 * 60 * 60 * 1000;

/** Lê os dois campos que a API manda em `dados` de `/Conversas/{id}/Mensagens`. */
export function lerJanela(dados: unknown): JanelaAtendimento | null {
  if (!dados || typeof dados !== 'object' || Array.isArray(dados)) return null;
  const d = dados as { janelaAplica?: unknown; janelaFechaEm?: unknown };
  // API antiga, sem os campos: não trava nada.
  if (typeof d.janelaAplica !== 'boolean') return null;
  const fechaEm = typeof d.janelaFechaEm === 'string' ? Date.parse(d.janelaFechaEm) : NaN;
  return { aplica: d.janelaAplica, fechaEm: Number.isNaN(fechaEm) ? null : fechaEm };
}

/** O cliente acabou de escrever: a janela reabre por 24h a partir de agora. */
export function reabrirJanela(janela: JanelaAtendimento | null): JanelaAtendimento | null {
  return janela?.aplica ? { aplica: true, fechaEm: Date.now() + DURACAO_JANELA_MS } : janela;
}

const formatarQuando = (ms: number) => {
  const d = new Date(ms);
  const data = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data} às ${hora}`;
};

/**
 * Devolve o motivo do bloqueio, ou `null` se dá para enviar. Vira sozinho no
 * minuto em que a janela fecha, sem precisar recarregar a conversa.
 */
export function useJanelaAtendimento(janela: JanelaAtendimento | null): string | null {
  const [agora, setAgora] = useState(() => Date.now());

  const fechaEm = janela?.aplica ? janela.fechaEm : null;

  useEffect(() => {
    setAgora(Date.now());
    if (fechaEm === null) return;
    const falta = fechaEm - Date.now();
    if (falta <= 0) return;
    // setTimeout estoura acima de ~24,8 dias; a janela nunca passa de 24h.
    const t = window.setTimeout(() => setAgora(Date.now()), falta + 50);
    return () => window.clearTimeout(t);
  }, [fechaEm]);

  if (!janela?.aplica) return null;
  if (janela.fechaEm === null) {
    return 'O cliente ainda não escreveu nesta conversa. O WhatsApp só permite iniciar o contato com um template aprovado.';
  }
  if (agora < janela.fechaEm) return null;
  return `A janela de 24 horas fechou em ${formatarQuando(janela.fechaEm)}. O WhatsApp só permite retomar a conversa com um template aprovado.`;
}
